import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { GearEditor } from '@/components/GearEditor';
import { Button } from '@/components/ui/button';
import { Package, ChevronDown, ChevronUp, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const MyGearCard = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [gear, setGear] = useState(() => (user?.equipment || []).map((g) => ({
    type: g.type || 'kite', brand: g.brand || '', size: g.size || '', model: g.model || '',
  })));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const equipment = gear.filter((g) => g.brand || g.size || g.model);
      await api.put('/users/me/gear', { equipment });
      await refreshUser();
      toast.success(t('myGear.saved'));
      setOpen(false);
    } catch (e) {
      toast.error(t('myGear.saveError'));
    } finally { setSaving(false); }
  };

  const summary = (user?.equipment || []);

  return (
    <div className="kite-card p-5" data-testid="my-gear-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[#00B4D8]/12 flex items-center justify-center"><Package className="w-5 h-5 text-[#00B4D8]" /></div>
          <div>
            <h3 className="font-semibold">{t('myGear.title')}</h3>
            <p className="text-xs text-muted-foreground">{summary.length ? t('myGear.items', { count: summary.length }) : t('myGear.empty')}</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setOpen((o) => !o)} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl" data-testid="my-gear-toggle">
          {open ? <>{t('myGear.close')} <ChevronUp className="w-4 h-4 ml-1" /></> : <>{summary.length ? t('myGear.manage') : t('myGear.add')} <ChevronDown className="w-4 h-4 ml-1" /></>}
        </Button>
      </div>

      {/* summary chips when collapsed */}
      {!open && summary.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {summary.map((g, i) => (
            <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#0A0A0A] border border-white/10 text-white/85">
              <span className="text-[#48CAE4]">{t(`myGear.types.${g.type}`, { defaultValue: g.type })}</span>
              {g.brand ? ` · ${g.brand}` : ''}{g.size ? ` ${g.size}` : ''}{g.model ? ` ${g.model}` : ''}
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="mt-5">
          <GearEditor value={gear} onChange={setGear} />
          <div className="flex justify-end mt-4">
            <Button onClick={save} disabled={saving} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="my-gear-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t('myGear.saveGear')}</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
